// src/components/DocumentPDF.jsx
// Génération PDF côté navigateur — jsPDF + jsPDF-AutoTable
// Architecture : autoTable gère 100% la pagination via margin.top + didDrawPage

// ── Chargement dynamique des libs ─────────────────────────────
async function loadLibs() {
  if (!window.jspdf) {
    await new Promise((res, rej) => {
      const s = document.createElement('script')
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
      s.onload = res; s.onerror = rej
      document.head.appendChild(s)
    })
  }
  if (!window.jspdf?.jsPDF?.API?.autoTable) {
    await new Promise((res, rej) => {
      const s = document.createElement('script')
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js'
      s.onload = res; s.onerror = rej
      document.head.appendChild(s)
    })
  }
  return window.jspdf.jsPDF
}

// ── Constantes mise en page ────────────────────────────────────
const PAGE_W = 210
const PAGE_H = 297
const ML = 14   // marge gauche
const MR = 14   // marge droite
const MB = 22   // marge bas (footer)
const COL_W = PAGE_W - ML - MR  // 182 mm

// Hauteurs des headers (marges top pour autoTable)
const HEADER_H_P1 = 105  // page 1 : grand header (bande + émetteur + client + infos)
const HEADER_H_NEXT = 18   // pages suivantes : mini header

// Layout "D" — classique/sobre (format proposé par le client)
const HEADER_H_P1_CLASSIQUE = 84
const HEADER_H_NEXT_CLASSIQUE = 16

// ── Palette couleurs ───────────────────────────────────────────
const PALETTE = {
  vert: [46, 125, 82],
  gris: [84, 110, 122],
  bleu: [21, 101, 192],
  lavande: [94, 53, 177],
  jaune: [249, 168, 37],
}

const C = {
  primary: [30, 100, 168],
  primaryLight: [235, 243, 252],
  accent: [0, 168, 120],
  text: [25, 35, 50],
  textMid: [90, 105, 120],
  textLight: [140, 155, 170],
  border: [215, 225, 235],
  rowOdd: [247, 250, 253],
  white: [255, 255, 255],
  danger: [210, 50, 60],
  dangerLight: [254, 235, 237],
}

const STATUT_COLORS = {
  brouillon: [180, 180, 180],
  envoye: [30, 100, 168],
  accepte: [0, 150, 100],
  refuse: [210, 50, 60],
  en_attente: [200, 130, 0],
  partiel: [200, 130, 0],
  regle: [0, 150, 100],
  annule: [150, 50, 50],
  transforme: [100, 80, 160],
}

const TYPES_FR = {
  devis: 'DEVIS',
  facture: 'FACTURE',
  bon_commande: 'BON DE COMMANDE',
  bon_livraison: 'BON DE LIVRAISON',
  avoir: 'AVOIR',
  facture_acompte: "FACTURE D'ACOMPTE",
}

const STATUTS_FR = {
  brouillon: 'Brouillon', envoye: 'Envoyé',
  accepte: 'Accepté', refuse: 'Refusé',
  en_attente: 'En attente', partiel: 'Partiel',
  regle: 'Réglé', annule: 'Annulé',
  transforme: 'Transformé',
}

const DEVISE_SYMBOLS = { EUR: 'EUR', XOF: 'FCFA', USD: 'USD', GBP: 'GBP', MAD: 'MAD' }

// ── Helpers ────────────────────────────────────────────────────
const fmt = n => {
  const [i, d] = (parseFloat(n) || 0).toFixed(2).split('.')
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + d
}
const fmtDate = d => {
  if (!d) return '-'
  const dt = new Date(d)
  return [dt.getDate(), dt.getMonth() + 1, dt.getFullYear()]
    .map((v, idx) => idx < 2 ? String(v).padStart(2, '0') : v).join('/')
}
const fmtMoney = (n, devise) =>
  fmt(n) + '\u00A0' + (DEVISE_SYMBOLS[devise] || devise || 'EUR')

const rgb2hex = ([r, g, b]) =>
  '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')

// ══════════════════════════════════════════════════════════════
//  GÉNÉRATEUR PRINCIPAL
// ══════════════════════════════════════════════════════════════
export async function generateDocumentPDF(doc, mode = 'download') {
  const JsPDF = await loadLibs()
  const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const devise = doc.monnaie || 'EUR'
  const typeLabel = TYPES_FR[doc.type] || doc.type?.toUpperCase() || 'DOCUMENT'

  const couleurNom = doc.pdf_couleur || 'vert'
  const couleurRGB = PALETTE[couleurNom] || PALETTE.vert
  const couleurLight = couleurRGB.map(v => Math.min(255, v + 170))

  // Layout sélectionné dans Paramètres → Options PDF
  // 'A' = moderne (bandeau coloré, existant) — 'D' = classique/sobre (format client)
  const layout = doc.pdf_layout || 'A'
  const isClassique = layout === 'D'


  // Charger le logo en base64 si disponible
  if (doc.user_logo_url) {
    try {
      const resp = await fetch(doc.user_logo_url)
      const blob = await resp.blob()
      doc._logoB64 = await new Promise(res => {
        const r = new FileReader()
        r.onload = () => res(r.result)
        r.readAsDataURL(blob)
      })
      doc._logoFmt = blob.type === 'image/png' ? 'PNG' : 'JPEG'
    } catch { /* pas de logo, on continue */ }
  }

  // ── Raccourcis dessin ───────────────────────────────────────
  const fill = ([r, g, b]) => pdf.setFillColor(r, g, b)
  const draw = ([r, g, b]) => pdf.setDrawColor(r, g, b)
  const clr = ([r, g, b]) => pdf.setTextColor(r, g, b)
  const font = (style = 'normal', size = 9) => { pdf.setFont('helvetica', style); pdf.setFontSize(size) }
  const tx = (t, x, y, opts = {}) => pdf.text(String(t ?? ''), x, y, opts)
  const rect = (x, y, w, h, col) => { fill(col); pdf.rect(x, y, w, h, 'F') }
  const hline = (y, x1 = ML, x2 = PAGE_W - MR, col = C.border, lw = 0.2) => {
    draw(col); pdf.setLineWidth(lw); pdf.line(x1, y, x2, y)
  }

  // ══════════════════════════════════════════════════════════════
  //  HEADER PAGE 1 — dessiné dans la zone 0..HEADER_H_P1
  // ══════════════════════════════════════════════════════════════
  function drawHeader1() {
    if (isClassique) { drawHeader1Classique(); return }

    // Bande couleur top
    rect(0, 0, PAGE_W, 28, couleurRGB)

    // Nom société ou logo
    if (doc._logoB64) {
      try {
        pdf.addImage(doc._logoB64, doc._logoFmt || 'JPEG', ML, 4, 0, 20)
      } catch { /* fallback texte */ }
    }
    font('bold', 17); clr(C.white)
    const societeNom = doc.user_societe || `${doc.user_prenom || ''} ${doc.user_nom || ''}`.trim() || 'Mon entreprise'
    tx(doc._logoB64 ? '' : societeNom, ML, 15)

    // Type + numéro
    font('bold', 24)
    tx(typeLabel, PAGE_W - MR, 13, { align: 'right' })
    font('normal', 9)
    tx(`N°\u00A0${doc.numero}`, PAGE_W - MR, 22, { align: 'right' })

    // Émetteur
    let ey = 34
    font('normal', 7.5); clr(C.textMid)
      ;[
        doc.user_activite,
        doc.user_adresse,
        [doc.user_cp, doc.user_ville].filter(Boolean).join(' '),
        doc.user_tel,
        doc.user_email,
        doc.user_siret ? `SIRET : ${doc.user_siret}` : null,
        doc.user_tva_intra ? `N° TVA : ${doc.user_tva_intra}` : null,
      ].filter(Boolean).forEach(l => { tx(l, ML, ey); ey += 4 })

    // Bloc client
    const cX = 105
    const cY = 32
    const cW = PAGE_W - MR - cX + 4
    rect(cX - 5, cY - 5, cW + 5, 44, couleurLight)
    draw(couleurRGB); pdf.setLineWidth(0.4)
    pdf.rect(cX - 5, cY - 5, cW + 5, 44, 'S')

    font('bold', 7); clr(couleurRGB)
    tx('DESTINATAIRE', cX, cY)
    font('bold', 10); clr(C.text)
    tx(doc.contact_nom || '—', cX, cY + 7)

    font('normal', 8.5); clr(C.textMid)
    let cy = cY + 14
      ;[
        doc.contact_adresse,
        [doc.contact_cp, doc.contact_ville].filter(Boolean).join(' '),
        doc.contact_pays !== 'France' ? doc.contact_pays : null,
        doc.contact_email,
        doc.contact_siret ? `SIRET : ${doc.contact_siret}` : null,
        doc.contact_tva_intra ? `N° TVA : ${doc.contact_tva_intra}` : null,
      ].filter(Boolean).forEach(l => { tx(l, cX, cy); cy += 4.5 })

    // Bandeau infos document — positionné à infoY fixe = 92
    const infoY = 88
    rect(ML, infoY, COL_W, 16, couleurLight)
    draw(C.border); pdf.setLineWidth(0.2)
    pdf.rect(ML, infoY, COL_W, 16, 'S')

    const infoItems = [
      { label: 'DATE', value: fmtDate(doc.date_document) },
      doc.date_validite ? { label: 'VALIDITÉ', value: fmtDate(doc.date_validite) } : null,
      doc.date_echeance ? { label: 'ÉCHÉANCE', value: fmtDate(doc.date_echeance) } : null,
      doc.reference_client ? { label: 'RÉF. CLIENT', value: doc.reference_client } : null,
      doc.mode_paiement ? { label: 'PAIEMENT', value: doc.mode_paiement } : null,
      { label: 'STATUT', value: STATUTS_FR[doc.statut] || doc.statut || '—' },
    ].filter(Boolean)

    const iW = COL_W / infoItems.length
    infoItems.forEach((item, i) => {
      const ix = ML + i * iW + 3
      font('bold', 6.5); clr(couleurRGB)
      tx(item.label, ix, infoY + 5)
      if (item.label === 'STATUT') {
        const sBg = STATUT_COLORS[doc.statut] || C.textMid
        rect(ix - 1, infoY + 7, iW - 6, 6, sBg)
        font('bold', 7); clr(C.white)
        tx(item.value, ix + (iW - 6) / 2 - 1, infoY + 11.5, { align: 'center' })
      } else {
        font('bold', 8.5); clr(C.text)
        tx(item.value, ix, infoY + 12)
      }
    })
  }

  // ══════════════════════════════════════════════════════════════
  //  HEADER PAGE 1 — LAYOUT CLASSIQUE (format proposé par le client)
  //  Logo + coordonnées en clair / encart léger type+n°+date / bloc
  //  client sans cadre / ligne d'objet / pas de bandeau coloré plein
  // ══════════════════════════════════════════════════════════════
  function drawHeader1Classique() {
    const boxX = 122
    const boxW = PAGE_W - MR - boxX

    // Logo ou nom société, en haut à gauche
    let leftY = 10
    if (doc._logoB64) {
      try {
        pdf.addImage(doc._logoB64, doc._logoFmt || 'JPEG', ML, 6, 0, 20)
        leftY = 30
      } catch { leftY = 10 }
    }
    if (!doc._logoB64) {
      font('bold', 15); clr(couleurRGB)
      tx(doc.user_societe || `${doc.user_prenom || ''} ${doc.user_nom || ''}`.trim() || 'Mon entreprise', ML, leftY)
      leftY += 8
    }

    // Coordonnées émetteur, texte simple sous le logo
    font('normal', 7.5); clr(C.textMid)
    let ey = leftY
      ;[
        doc.user_activite,
        doc.user_tel ? `Tel : ${doc.user_tel}` : null,
        doc.user_mobile ? `Cel : ${doc.user_mobile}` : null,
        doc.user_email,
        doc.user_ville,
      ].filter(Boolean).forEach(l => { tx(l, ML, ey); ey += 3.8 })

    // Encart léger : type de document + n° + date
    rect(boxX, 6, boxW, 24, couleurLight)
    font('bold', 15); clr(C.text)
    tx(typeLabel.split('').join('\u200A'), boxX + 4, 15)
    font('normal', 8); clr(C.textMid)
    tx('n°:', boxX + 4, 22)
    font('bold', 8.5); clr(C.text)
    tx(String(doc.numero || ''), boxX + 12, 22)
    font('normal', 8); clr(C.textMid)
    tx('Date:', boxX + 4, 27.5)
    font('bold', 8.5); clr(C.text)
    tx(fmtDate(doc.date_document), boxX + 15, 27.5)

    // Bloc destinataire — sans cadre, sous l'encart, aligné à droite
    let cy = 40
    font('bold', 10.5); clr(C.text)
    tx(doc.contact_nom || '—', boxX, cy); cy += 6
    font('normal', 8.5); clr(C.textMid)
      ;[
        doc.contact_adresse,
        [doc.contact_cp, doc.contact_ville].filter(Boolean).join(' '),
        doc.contact_pays !== 'France' ? doc.contact_pays : null,
      ].filter(Boolean).forEach(l => { tx(l, boxX, cy); cy += 4.5 })

    // Ligne d'objet — titre en gras pleine largeur
    const objet = doc.objet || doc.titre || doc.description_courte || ''
    let objY = Math.max(ey + 4, cy + 4, 62)
    if (objet) {
      font('bold', 10.5); clr(C.text)
      tx(objet, ML, objY)
      objY += 5
    }
    hline(objY + 2, ML, PAGE_W - MR, C.border, 0.3)
  }

  // ══════════════════════════════════════════════════════════════
  //  MINI HEADER pages 2+ — dessiné dans la zone 0..HEADER_H_NEXT
  // ══════════════════════════════════════════════════════════════
  function drawHeaderNext() {
    if (isClassique) {
      font('bold', 8.5); clr(C.text)
      tx(`${typeLabel}\u00A0N°\u00A0${doc.numero}  —  ${doc.contact_nom || ''}`, ML, 9)
      font('normal', 8); clr(C.textMid)
      tx(fmtDate(doc.date_document), PAGE_W - MR, 9, { align: 'right' })
      hline(HEADER_H_NEXT_CLASSIQUE - 4, ML, PAGE_W - MR, couleurRGB, 0.5)
      return
    }
    rect(0, 0, PAGE_W, HEADER_H_NEXT - 2, couleurRGB)
    font('bold', 8); clr(C.white)
    tx(`${typeLabel}\u00A0N°\u00A0${doc.numero}  —  ${doc.contact_nom || ''}`, ML, 10)
    tx(fmtDate(doc.date_document), PAGE_W - MR, 10, { align: 'right' })
  }

  // ══════════════════════════════════════════════════════════════
  //  FOOTER — dessiné dans la zone PAGE_H-MB..PAGE_H
  // ══════════════════════════════════════════════════════════════
  function drawFooter(pageNum, pageCount) {
    if (isClassique) { drawFooterClassique(pageNum, pageCount); return }

    const fy = PAGE_H - MB
    rect(0, fy, PAGE_W, MB, couleurRGB)
    font('normal', 6.5); clr(C.white)

    const piedText = doc.mention_pied || doc.user_pied || ''
    if (piedText) {
      const lines = pdf.splitTextToSize(piedText, COL_W * 0.55)
      tx(lines[0] || '', ML, fy + 8)
    }
    const mentionTVA = doc.user_tva_intra ? `TVA intracommunautaire : ${doc.user_tva_intra}` : ''
    if (mentionTVA) tx(mentionTVA, PAGE_W / 2, fy + 8, { align: 'center' })

    if (doc.user_mentions_obligatoires) {
      font('normal', 6); clr(C.white)
      const mLines = pdf.splitTextToSize(doc.user_mentions_obligatoires, COL_W * 0.6)
      mLines.slice(0, 2).forEach((l, i) => tx(l, ML, fy + 13 + i * 3.5))
    }
    if (doc.user_info_supplementaire) {
      font('italic', 6); clr(C.white)
      tx(doc.user_info_supplementaire.substring(0, 120), ML, fy + 20)
    }

    font('bold', 7)
    tx(`Page ${pageNum} / ${pageCount}`, PAGE_W - MR, fy + 8, { align: 'right' })
  }

  // ── FOOTER — LAYOUT CLASSIQUE : pas de bandeau plein, ligne fine + texte centré gris ──
  function drawFooterClassique(pageNum, pageCount) {
    const fy = PAGE_H - MB + 4
    hline(fy, ML, PAGE_W - MR, C.border, 0.3)

    font('normal', 6.5); clr(C.textLight)
    const l1 = doc.pied_de_page || doc.mention_pied || doc.user_pied || ''
    const l2 = doc.user_mentions_obligatoires || ''
    if (l1) tx(pdf.splitTextToSize(l1, COL_W)[0] || '', PAGE_W / 2, fy + 6, { align: 'center' })
    if (l2) tx(pdf.splitTextToSize(l2, COL_W)[0] || '', PAGE_W / 2, fy + 10.5, { align: 'center' })

    font('bold', 7); clr(C.textMid)
    tx(`Page ${pageNum} / ${pageCount}`, PAGE_W - MR, fy + 6, { align: 'right' })
  }

  // ══════════════════════════════════════════════════════════════
  //  CONSTRUCTION DES LIGNES DU TABLEAU
  // ══════════════════════════════════════════════════════════════
  const lignes = doc.lignes || []
  const tableBody = []

  lignes.forEach(ligne => {
    if (ligne.type_ligne === 'saut') {
      tableBody.push([{
        content: '',
        colSpan: 6,
        styles: { minCellHeight: 5, fillColor: rgb2hex(C.white), lineWidth: 0 },
      }])
      return
    }
    if (ligne.type_ligne === 'texte') {
      tableBody.push([{
        content: ligne.designation || '',
        colSpan: 6,
        styles: {
          fontStyle: 'italic',
          textColor: rgb2hex(C.textMid),
          fontSize: 8,
          fillColor: rgb2hex(C.white),
          lineWidth: 0,
        },
      }])
      return
    }

    // Ligne produit normale
    const remise = parseFloat(ligne.remise_pct) || 0
    tableBody.push([
      { content: ligne.designation || '' },
      { content: String(parseFloat(ligne.quantite) || 1), styles: { halign: 'center' } },
      { content: fmt(ligne.prix_ht), styles: { halign: 'right' } },
      {
        content: remise > 0 ? `${remise}%` : '—',
        styles: {
          halign: 'right',
          textColor: remise > 0 ? rgb2hex(C.danger) : rgb2hex(C.textLight),
          fontStyle: remise > 0 ? 'bold' : 'normal',
        },
      },
      { content: `${parseFloat(ligne.tva_taux) || 0}%`, styles: { halign: 'right' } },
      { content: fmt(ligne.total_ht), styles: { halign: 'right', fontStyle: 'bold' } },
    ])
  })

  // ══════════════════════════════════════════════════════════════
  //  AUTOTABLE — laisse autoTable gérer 100% la pagination
  //
  //  margin.top page 1  = HEADER_H_P1   → tableau commence sous le grand header
  //  margin.top pages + = HEADER_H_NEXT → tableau commence sous le mini header
  //
  //  didDrawPage est appelé AVANT de remplir chaque page →
  //  on dessine header + footer, puis autoTable remplit la zone libre
  // ══════════════════════════════════════════════════════════════
  // Layout classique : autoTable ne gère pas un "contour extérieur" façon
  // border-collapse CSS (seul l'en-tête a des bordures de cellule, le corps
  // n'en a aucune). On trace donc ce contour à la main, page par page, en
  // suivant l'étendue verticale réelle des cellules dessinées (didDrawCell).
  const classiqueTableBounds = {}

  pdf.autoTable({
    theme: isClassique ? 'plain' : 'striped',

    startY: isClassique ? HEADER_H_P1_CLASSIQUE : HEADER_H_P1,   // page 1 : commence après le grand header

    margin: {
      top: isClassique ? HEADER_H_NEXT_CLASSIQUE : HEADER_H_NEXT,  // pages 2+ : commence après le mini header
      left: ML,
      right: MR,
      bottom: MB + 2,         // espace pour le footer
    },

    head: [[
      { content: 'DÉSIGNATION', styles: { halign: 'left' } },
      { content: 'QTÉ', styles: { halign: 'center' } },
      { content: 'P.U. HT', styles: { halign: 'right' } },
      { content: 'REMISE', styles: { halign: 'right' } },
      { content: 'TVA', styles: { halign: 'right' } },
      { content: 'TOTAL HT', styles: { halign: 'right' } },
    ]],

    body: tableBody,

    columnStyles: {
      0: { cellWidth: 74 },
      1: { cellWidth: 18, halign: 'center' },
      2: { cellWidth: 28, halign: 'right' },
      3: { cellWidth: 18, halign: 'right' },
      4: { cellWidth: 16, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' },
    },

    headStyles: isClassique ? {
      fillColor: rgb2hex(couleurLight),
      textColor: rgb2hex(C.text),
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
      lineWidth: 0.2,
      lineColor: rgb2hex(couleurRGB),
    } : {
      fillColor: rgb2hex(couleurRGB),
      textColor: '#ffffff',
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
      lineWidth: 0,
    },

    bodyStyles: {
      fontSize: 8.5,
      textColor: rgb2hex(C.text),
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
      lineColor: rgb2hex(isClassique ? couleurRGB : C.border),
      // Classique : uniquement bordures gauche/droite → les séparateurs de
      // colonnes du header se prolongent en continu jusqu'en bas, sans
      // ligne horizontale entre les lignes du corps.
      lineWidth: isClassique ? { top: 0, bottom: 0, left: 0.2, right: 0.2 } : 0.15,
    },

    alternateRowStyles: {
      // Layout classique : pas de zébrure, grille sobre comme le modèle du client
      fillColor: rgb2hex(isClassique ? C.white : C.rowOdd),
    },

    showHead: 'everyPage',
    rowPageBreak: 'auto',

    // ── didDrawCell : mémorise, page par page, le haut/bas réel du tableau ──
    didDrawCell: (data) => {
      if (!isClassique) return
      const p = data.pageNumber
      const top = data.cell.y
      const bottom = data.cell.y + data.cell.height
      if (!classiqueTableBounds[p]) {
        classiqueTableBounds[p] = { top, bottom }
      } else {
        classiqueTableBounds[p].top = Math.min(classiqueTableBounds[p].top, top)
        classiqueTableBounds[p].bottom = Math.max(classiqueTableBounds[p].bottom, bottom)
      }
    },

    // ── didDrawPage : appelé avant de remplir chaque page ──────
    didDrawPage: (data) => {
      if (data.pageNumber === 1) {
        drawHeader1()
      } else {
        drawHeaderNext()
      }
      // Footer : pageCount pas encore connu → on passe 0, corrigé en post-traitement
      drawFooter(data.pageNumber, 0)
    },
  })

  // ── Layout classique : ligne de fermeture en bas du tableau ──
  // Les côtés sont déjà fermés par les bordures gauche/droite des cellules
  // (continues du header jusqu'à la dernière ligne) ; il ne manque que le
  // trait horizontal du bas, absent puisque bodyStyles n'a pas de bottom.
  if (isClassique) {
    Object.entries(classiqueTableBounds).forEach(([p, b]) => {
      pdf.setPage(Number(p))
      hline(b.bottom, ML, ML + COL_W, couleurRGB, 0.3)
    })
    pdf.setPage(pdf.internal.getNumberOfPages())
  }

  // ── Post-traitement : réécrire les footers avec le bon pageCount ──
  const totalPages = pdf.internal.getNumberOfPages()
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p)
    drawFooter(p, totalPages)
  }

  // ══════════════════════════════════════════════════════════════
  //  BLOC TOTAUX — dessiné après pdf.lastAutoTable.finalY
  // ══════════════════════════════════════════════════════════════
  let y = pdf.lastAutoTable.finalY + 10

  // Nouvelle page si pas assez de place
  if (y + 65 > PAGE_H - MB - 5) {
    pdf.addPage()
    drawHeaderNext()
    drawFooter(pdf.internal.getNumberOfPages(), 0)
    y = HEADER_H_NEXT + 8
  }

  // ── Groupes TVA multi-taux ──────────────────────────────────
  const tvaGroups = {}
  lignes.forEach(l => {
    if (l.type_ligne === 'texte' || l.type_ligne === 'saut') return
    const key = `${parseFloat(l.tva_taux) || 0}%`
    tvaGroups[key] = (tvaGroups[key] || 0) + parseFloat(l.total_tva || 0)
  })
  const isMultiTVA = Object.keys(tvaGroups).length > 1

  const totLines = [
    { label: 'Total HT', value: fmtMoney(doc.total_ht, devise) },
  ]
  if (parseFloat(doc.total_remise) > 0) {
    totLines.push({ label: 'Remise globale', value: `- ${fmtMoney(doc.total_remise, devise)}`, danger: true })
  }
  if (isMultiTVA) {
    Object.entries(tvaGroups).forEach(([taux, montant]) =>
      totLines.push({ label: `TVA ${taux}`, value: fmtMoney(montant, devise) }))
  } else {
    const tauxUnique = Object.keys(tvaGroups)[0] || ''
    totLines.push({ label: tauxUnique ? `TVA (${tauxUnique})` : 'TVA', value: fmtMoney(doc.total_tva, devise) })
  }

  // ── Rendu bloc totaux ───────────────────────────────────────
  const totX = PAGE_W - MR - 74
  const totW = 74

  if (isClassique) {
    // Sobre : pas d'encadré, simples lignes séparées par un filet fin
    totLines.forEach(line => {
      font('normal', 8.5)
      clr(line.danger ? C.danger : C.textMid)
      tx(line.label, totX + 2, y + 4)
      clr(line.danger ? C.danger : C.text)
      tx(line.value, totX + totW - 2, y + 4, { align: 'right' })
      hline(y + 7, totX, totX + totW, C.border, 0.15)
      y += 8
    })

    y += 3
    hline(y, totX, totX + totW, couleurRGB, 0.7)
    y += 6
    font('bold', 11.5); clr(C.text)
    tx('Total TTC', totX + 2, y + 3)
    tx(fmtMoney(doc.total_ttc, devise), totX + totW - 2, y + 3, { align: 'right' })
    y += 12

    if (parseFloat(doc.montant_regle) > 0) {
      font('normal', 8.5); clr(C.textMid)
      tx('Déjà réglé', totX + 2, y + 4)
      clr(C.accent); font('bold', 8.5)
      tx(fmtMoney(doc.montant_regle, devise), totX + totW - 2, y + 4, { align: 'right' })
      y += 8
      font('bold', 9.5); clr(C.danger)
      tx('Solde dû', totX + 2, y + 4)
      tx(fmtMoney(doc.total_ttc - doc.montant_regle, devise), totX + totW - 2, y + 4, { align: 'right' })
      y += 10
    }
  } else {
    const totBgH = totLines.length * 8 + 24

    rect(totX - 3, y - 3, totW + 7, totBgH, couleurLight)
    draw(C.border); pdf.setLineWidth(0.2)
    pdf.rect(totX - 3, y - 3, totW + 7, totBgH, 'S')

    totLines.forEach(line => {
      font('normal', 8.5)
      clr(line.danger ? C.danger : C.textMid)
      tx(line.label, totX + 2, y + 4)
      clr(line.danger ? C.danger : C.text)
      tx(line.value, totX + totW - 2, y + 4, { align: 'right' })
      hline(y + 7, totX - 3, totX + totW + 4, C.border, 0.15)
      y += 8
    })

    y += 3
    hline(y, totX - 3, totX + totW + 4, couleurRGB, 0.6)
    y += 4

    rect(totX - 3, y - 1, totW + 7, 14, couleurRGB)
    font('bold', 12); clr(C.white)
    tx('TOTAL TTC', totX + 2, y + 8)
    tx(fmtMoney(doc.total_ttc, devise), totX + totW - 2, y + 8, { align: 'right' })
    y += 17

    if (parseFloat(doc.montant_regle) > 0) {
      font('normal', 8.5); clr(C.textMid)
      tx('Déjà réglé', totX + 2, y + 4)
      clr(C.accent); font('bold', 8.5)
      tx(fmtMoney(doc.montant_regle, devise), totX + totW - 2, y + 4, { align: 'right' })
      y += 9

      rect(totX - 3, y - 2, totW + 7, 12, C.dangerLight)
      draw(C.danger); pdf.setLineWidth(0.4)
      pdf.rect(totX - 3, y - 2, totW + 7, 12, 'S')
      font('bold', 10); clr(C.danger)
      tx('SOLDE DÛ', totX + 2, y + 6)
      tx(fmtMoney(doc.total_ttc - doc.montant_regle, devise), totX + totW - 2, y + 6, { align: 'right' })
      y += 14
    }
  }

  // ── Conditions (devis) — 4 champs du document, sinon valeurs par défaut des Paramètres ──
  const condItems = doc.type === 'devis' ? [
    { label: 'Délai de livraison', value: doc.delai_livraison || doc.user_delai_livraison },
    { label: 'Garantie sur les équipements', value: doc.garantie_equipements || doc.user_garantie_equipements },
    { label: 'Garantie sur les installations', value: doc.garantie_installations || doc.user_garantie_installations },
    { label: 'Conditions de paiement', value: doc.conditions_paiement || doc.user_conditions_paiement },
  ].filter(i => i.value) : []

  let condBottomY = y
  if (condItems.length) {
    let condStartY = isClassique ? y + 8 : pdf.lastAutoTable.finalY + 10
    const condH = (isClassique ? 0 : 5.5) + condItems.length * 4.6 + 8
    if (condStartY + condH > PAGE_H - MB - 5) {
      pdf.addPage()
      drawHeaderNext()
      drawFooter(pdf.internal.getNumberOfPages(), 0)
      condStartY = (isClassique ? HEADER_H_NEXT_CLASSIQUE : HEADER_H_NEXT) + 8
    }
    let cy = condStartY + 4
    if (!isClassique) {
      font('bold', 7.5); clr(couleurRGB)
      tx('CONDITIONS', ML, cy)
      cy += 5.5
    }
    font('normal', 8); clr(C.text)
    condItems.forEach(item => {
      tx(`${item.label} : ${item.value}`, ML, cy)
      cy += 4.6
    })
    condBottomY = cy
  }

  // ── Commentaire (bon de livraison / bon de commande) — pleine largeur ──
  const isBonType = doc.type === 'bon_livraison' || doc.type === 'bon_commande'
  const commentText = doc.commentaire || (isBonType ? doc.user_commentaire_bon : null)
  let closeY = Math.max(y, condBottomY)
  if (commentText && isBonType) {
    closeY += 10
    if (closeY > PAGE_H - MB - 30) {
      pdf.addPage()
      drawHeaderNext()
      drawFooter(pdf.internal.getNumberOfPages(), 0)
      closeY = (isClassique ? HEADER_H_NEXT_CLASSIQUE : HEADER_H_NEXT) + 10
    }
    font('normal', 8.5); clr(C.text)
    const commentLines = pdf.splitTextToSize(commentText, COL_W)
    pdf.text(commentLines, ML, closeY)
    closeY += commentLines.length * 4.5
  }

  // ── Bloc signature — layout classique uniquement ─────────────
  if (isClassique) {
    let sigY = closeY + 14
    if (sigY > PAGE_H - MB - 12) {
      pdf.addPage()
      drawHeaderNext()
      drawFooter(pdf.internal.getNumberOfPages(), 0)
      sigY = HEADER_H_NEXT_CLASSIQUE + 16
    }
    if (isBonType) {
      // Double signature : client (confirmation) à gauche, société à droite
      font('bold', 9); clr(C.text)
      tx((doc.contact_nom || 'CLIENT').toUpperCase(), ML + 6, sigY, { align: 'left' })
      tx((doc.user_societe || 'SOCIÉTÉ').toUpperCase(), PAGE_W - MR - 55, sigY, { align: 'left' })
    } else {
      font('bold', 9); clr(C.text)
      tx('LA DIRECTION', PAGE_W / 2, sigY, { align: 'center' })
      font('normal', 7.5); clr(C.textLight)
      tx(doc.user_societe || '', PAGE_W / 2, sigY + 14, { align: 'center' })
    }
  }

  // ── Footer final avec pageCount correct ─────────────────────
  const finalTotal = pdf.internal.getNumberOfPages()
  for (let p = 1; p <= finalTotal; p++) {
    pdf.setPage(p)
    drawFooter(p, finalTotal)
  }

  // ── Sauvegarde ───────────────────────────────────────────────
  const filename = `${doc.numero || 'document'}_${(doc.contact_nom || '').replace(/\s+/g, '_') || 'client'}.pdf`
  if (mode === 'preview') {
    const blobUrl = pdf.output('bloburl')
    window.open(blobUrl, '_blank')
    return blobUrl
  }
  pdf.save(filename)
  return filename
}